import React from "react";

import { useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { observer } from "mobx-react";

import { LoginRequestDto } from "@api-types/user/LoginRequestDto";
import {
	FormInputField,
	PasswordInput,
} from "@components-shared/desktop/inputs";
import ErrorBoundaryWithPropagation from "@components/boundary/ErrorBoundaryWithPropagation";
import { FormButton } from "@components/buttons";
import { CheckboxField } from "@components/inputs/desktop";
import { LoginStore } from "@state/auth";
import { useAppContext } from "@state/hooks/shared";
import { THEME_SETTINGS } from "@theme-settings";
import { isNullOrWhitespace } from "@utils";
import { LoginType } from "@utils/common/loginTypeEnum";

import { LoginErrorMessage } from ".";

export default observer(function LoginForm({ store }: { store: LoginStore }) {
	const { culture } = useAppContext();

	const { handleSubmit } = useFormContext<LoginRequestDto>();

	const { t } = useTranslation("login");

	return (
		<ErrorBoundaryWithPropagation componentName={"Desktop/LoginForm.tsx"}>
			<form
				className="card--onboarding__content card--onboarding__content--shrink"
				onSubmit={handleSubmit((data) =>
					store.handleLoginSubmit(data, LoginType.DEFAULT)
				)}
			>
				<div className="form__group">
					<FormInputField
						id="loginUsername"
						name="username"
						className="form__field"
						label="login:EMAIL_OR_USERNAME_LABEL"
						labelClassName="label--strong"
						placeholder="login:EMAIL_OR_USERNAME_PLACEHOLDER"
						validate={{
							isNullOrWhitespace: (value: string) =>
								isNullOrWhitespace(value) !== true ||
								"COMMON.REQUIRED_FIELD",
							isEmail: (value: string) =>
								!value.includes("@") ||
								"login:USERNAME_MAIL_ERROR_MESSAGE",
						}}
						autoComplete="username"
						required
						tabIndex={1}
					/>
					<div className="u-type--right">
						<Link
							to={`/${culture}/auth/forgot-username`}
							className="u-anchor u-anchor--secondary u-anchor--underline u-type--xxsml"
						>
							{t<string>("FORGOT_USERNAME_LINK")}
						</Link>
					</div>
				</div>

				<div className="form__group">
					<PasswordInput
						id="loginPassword"
						className="form__field"
						label="login:PASSWORD_LABEL"
						labelClassName="label--strong"
						name="password"
						placeholder="login:PASSWORD_PLACEHOLDER"
						validate={{
							isNullOrWhitespace: (value: string) =>
								isNullOrWhitespace(value) !== true ||
								"COMMON.REQUIRED_FIELD",
						}}
						autoComplete="current-password"
						required
						tabIndex={2}
					/>
					<div className="u-type--right">
						<Link
							to={`/${culture}/auth/forgot-password`}
							className="u-anchor u-anchor--secondary u-anchor--underline u-type--xxsml"
						>
							{t<string>("FORGOT_PASSWORD_LINK")}
						</Link>
					</div>
				</div>

				<div className="form__group">
					<CheckboxField
						name="stayLoggedIn"
						id="sliCheck"
						label="login:STAY_LOGGED_IN"
					/>
				</div>

				<LoginErrorMessage loginError={store.loginError} />

				<FormButton
					atClassName="loginSubmitBtn"
					className="btn btn--base btn--block btn--accent"
					iconRight={THEME_SETTINGS.licensingIcons.includes("ggl")}
					iconClassName="u-icon--xmed u-icon--age-restriction u-color--accent--text"
					btnText={t("COMMON.LOGIN_BUTTON")}
				/>
			</form>
		</ErrorBoundaryWithPropagation>
	);
});
